import { useMutation, useQuery } from "@apollo/client";
import SearchUser from "components/_Pages/Maintainers/CorporateValue/Recognition/searchUser";
import StateMessage from "components/_Pages/Maintainers/CorporateValue/Recognition/stateMessage";
import IaButton from "components/core/iaButton/IaButton";
import {
  ComposeActions,
  ComposeSection,
  GlassTile,
  GlassTileGlobalStyles,
  resolveCorporateValueVisual,
  resolveFieldVariantForCorporateValue,
  Textarea,
  type FieldVariant,
  type PublishVariant,
} from "components/ui/v2";
import { gqlUser } from "gql";
import CorporateValues from "gql/CorporateValues";
import { SAVE_RECOGNITION } from "gql/CorporateValues/mutations";
import {
  IVendorUserList,
  GET_CORPORATE_VALUES_BY_VENDOR,
  ICorporateValueResponse
} from "gql/CorporateValues/queries";
import useTranslation from "next-translate/useTranslation";
import { useEffect, useMemo, useState } from "react";
import { useAllUserStore } from "stores/recognition/useAllUserStore";
import { getQueryOperator } from "utils/helpers";
export default function TabRecognition() {
  const minTextLength = 4;
  const timeOutMessage = 2500;
  const maxTextLength = 460;
  const { t } = useTranslation("common");

  const {
    data: corporateData,
    loading: loadingCorporate,
    error: errorCorporate
  } = useQuery<ICorporateValueResponse>(GET_CORPORATE_VALUES_BY_VENDOR);

  const { loadingAllUser, errorAllUser, dataAllUser } = useAllUserStore();
  const [selectedValueId, setSelectedValueId] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<IVendorUserList | null>(null);
  const [valueText, setValueText] = useState<string | undefined>(undefined);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [hasBeenSent, setHasBeenSent] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorMessages, setErrorMessages] = useState<string | undefined>(undefined);

  const [saveRecognition, { loading: loadingRecognition }] = useMutation(
    SAVE_RECOGNITION,
    {
      awaitRefetchQueries: true,
      refetchQueries: [
        getQueryOperator(gqlUser.queries.GET_FULL_USER),
        getQueryOperator(CorporateValues.queries.GET_RANKING_CORPORATE_VALUES)
      ]
    }
  );

  const submitRecognition = async () => {
    if (isSending) return;
    setIsSending(true);
    try {
      await saveRecognition({
        variables: {
          recognition: {
            corporateValue: selectedValueId,
            receivingUser: selectedUser?.id,
            message: valueText
          }
        }
      });
      setHasBeenSent(true);
      setValueText(undefined);
      setSelectedValueId(null);
    } catch (error) {
      setIsSending(false);
      if (error instanceof Error) setErrorMessages(error.message);
      setIsError(true);
      setHasBeenSent(false);
    } finally {
      setTimeout(() => setIsError(false), timeOutMessage);
      setTimeout(() => setHasBeenSent(false), timeOutMessage);
      setTimeout(() => setIsSending(false), 2000);
    }
  };

  useEffect(() => {
    const trimmed = valueText?.replace(/\s/g, "");
    setIsCompleted(
      !!selectedValueId &&
        !!selectedUser &&
        trimmed !== undefined &&
        trimmed.length >= minTextLength
    );
  }, [selectedValueId, selectedUser, valueText]);

  const handleSelectValue = (id: string) =>
    setSelectedValueId((prev) => (prev === id ? null : id));

  const corporateValues = corporateData?.getCorporateValuesByVendor;

  const fieldVariant: FieldVariant = useMemo(() => {
    if (!selectedValueId || !corporateValues?.length) return "slate";

    const index = corporateValues.findIndex((v) => v.id === selectedValueId);
    const selected = corporateValues[index];
    if (!selected) return "slate";

    return resolveFieldVariantForCorporateValue(selected.title, index);
  }, [selectedValueId, corporateValues]);

  const publishVariant = useMemo((): PublishVariant => {
    const map: Record<FieldVariant, PublishVariant> = {
      violet: "violet",
      sky: "sky",
      amber: "amber",
      emerald: "emerald",
      rose: "rose",
      slate: "indigo",
      subtle: "indigo",
    };
    return map[fieldVariant];
  }, [fieldVariant]);

  if (isError || errorCorporate || isSending || hasBeenSent) {
    return (
      <div className="px-3 py-4 sm:px-4 sm:py-5">
        {isError && <StateMessage variant="error" propMessage={errorMessages} />}
        {errorCorporate && <StateMessage variant="error" propMessage={errorCorporate.message} />}
        {isSending && <StateMessage variant="sending" />}
        {hasBeenSent && !isSending && <StateMessage variant="success" />}
      </div>
    );
  }

  return (
    <>
      {/* ── Valores corporativos ── */}
      <ComposeSection compact>
        {loadingCorporate ? (
          <div className="flex items-center gap-2 py-1">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-slate-200 border-t-amber-500" />
            <span className="text-[12px] text-slate-500 dark:text-zinc-400">{t("Cargando...")}</span>
          </div>
        ) : (
          <div className="flex flex-wrap justify-center gap-5 overflow-visible px-1 py-2 sm:gap-6">
            <GlassTileGlobalStyles />
            {corporateValues?.map((value, index) => {
              const isSelected = selectedValueId === value.id;
              const { variant, Icon } = resolveCorporateValueVisual(value.title, index);

              return (
                <GlassTile
                  key={value.id}
                  size="lg"
                  shape="circle"
                  label={value.title}
                  variant={variant}
                  selected={isSelected}
                  aria-label={value.title}
                  onClick={() => handleSelectValue(value.id)}
                  icon={<Icon size={30} strokeWidth={1.75} aria-hidden />}
                />
              );
            })}
            {!corporateValues?.length && (
              <p className="text-[12px] text-slate-500 dark:text-zinc-400">{t("No hay valores corporativos")}</p>
            )}
          </div>
        )}
      </ComposeSection>

      <ComposeSection compact className="mx-auto w-full max-w-xl space-y-5 px-5 sm:px-8">
        <SearchUser
          variant="small"
          inputVariant={fieldVariant}
          selectedUser={selectedUser}
          setSelectedUser={setSelectedUser}
          loading={loadingAllUser}
          error={errorAllUser}
          data={dataAllUser}
        />
        <Textarea
          size="sm"
          variant={fieldVariant}
          label={t("Motivo del reconocimiento")}
          value={valueText ?? ""}
          onChange={(e) => setValueText(e.target.value)}
          maxLength={maxTextLength}
          showCount
          insetCount
          className="resize-none"
          topEnd={
            <IaButton
              compact
              textToTransform={valueText}
              setTextToTransform={setValueText}
              type="textarea"
            />
          }
        />
      </ComposeSection>

      <ComposeActions
        compact
        onPublish={submitRecognition}
        loading={isSending || loadingRecognition}
        disabled={!isCompleted || isSending || loadingRecognition || loadingCorporate}
        loadingText={t("Enviando...")}
        publishText={t("Enviar")}
        publishVariant={publishVariant}
      />
    </>
  );
}
